// Browser-only loader that turns the AI-generated Corridor Zero PNGs (served
// from /game-sprites/corridor-zero/) into the engine's packed-RGBA SpriteFrame
// structures. This is the "baked art source" the procedural sprites.ts was
// always written to be swapped for — it emits the same ActorSprites shape.
//
// Everything here touches the DOM (Image, canvas), so it is only ever called
// from render-time paths, never from a game constructor. Under the headless
// test runner these functions are never invoked, and callers keep the
// procedural roster as a fallback.

import { aUnpack, packRGBA } from "./math";
import type { ActorSprites } from "./sprites";
import type { SpriteFrame } from "./types";

const BASE = "/game-sprites/corridor-zero";

/** Load one PNG and decode it to raw RGBA pixels via an offscreen canvas. */
async function loadPixels(
  name: string,
): Promise<{ data: Uint32Array; width: number; height: number }> {
  const img = new Image();
  // Same-origin static asset; no crossOrigin needed. Decode before drawing.
  img.src = `${BASE}/${name}.png`;
  await img.decode();
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d unavailable for sprite decode");
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);
  const raw = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = new Uint32Array(canvas.width * canvas.height);
  data.set(new Uint32Array(raw.data.buffer));
  return { data, width: canvas.width, height: canvas.height };
}

/** Tight bounding box of pixels with alpha above `threshold`. */
function alphaBounds(
  data: Uint32Array,
  width: number,
  height: number,
  threshold = 12,
): { x0: number; y0: number; x1: number; y1: number } {
  let x0 = width;
  let y0 = height;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (aUnpack(data[y * width + x]!) > threshold) {
        if (x < x0) x0 = x;
        if (y < y0) y0 = y;
        if (x > x1) x1 = x;
        if (y > y1) y1 = y;
      }
    }
  }
  if (x1 < x0 || y1 < y0) return { x0: 0, y0: 0, x1: width - 1, y1: height - 1 };
  return { x0, y0, x1, y1 };
}

/**
 * Crop a decoded image to its alpha bounding box and nearest-neighbour scale it
 * to `targetH` px tall (in-game sprites are small; this keeps the billboard
 * blit cheap and edges crisp). Returns a SpriteFrame with feet-on-floor anchor.
 */
function toFrame(
  src: { data: Uint32Array; width: number; height: number },
  targetH: number,
  anchorY: number,
): SpriteFrame {
  const { data, width, height } = src;
  const { x0, y0, x1, y1 } = alphaBounds(data, width, height);
  const cropW = x1 - x0 + 1;
  const cropH = y1 - y0 + 1;
  const scale = targetH / cropH;
  const outH = targetH;
  const outW = Math.max(1, Math.round(cropW * scale));
  const out = new Uint32Array(outW * outH);
  for (let y = 0; y < outH; y++) {
    const sy = y0 + Math.min(cropH - 1, (y / scale) | 0);
    for (let x = 0; x < outW; x++) {
      const sx = x0 + Math.min(cropW - 1, (x / scale) | 0);
      out[y * outW + x] = data[sy * width + sx]!;
    }
  }
  return { width: outW, height: outH, data: out, anchorX: 0.5, anchorY };
}

/** Copy a frame with each visible pixel tinted toward white (pain flash). */
function tintFlash(frame: SpriteFrame, amount: number): SpriteFrame {
  const out = new Uint32Array(frame.data.length);
  for (let i = 0; i < frame.data.length; i++) {
    const px = frame.data[i]!;
    const a = aUnpack(px);
    if (a === 0) {
      out[i] = px;
      continue;
    }
    const r = px & 0xff;
    const g = (px >> 8) & 0xff;
    const b = (px >> 16) & 0xff;
    const mix = (c: number): number => Math.min(255, (c + (255 - c) * amount) | 0);
    out[i] = packRGBA(mix(r), mix(g), mix(b), a);
  }
  return { ...frame, data: out };
}

/** Copy a frame with alpha scaled down (fade for the death sequence). */
function fade(frame: SpriteFrame, alphaScale: number): SpriteFrame {
  const out = new Uint32Array(frame.data.length);
  for (let i = 0; i < frame.data.length; i++) {
    const px = frame.data[i]!;
    const a = aUnpack(px);
    if (a === 0) {
      out[i] = px;
      continue;
    }
    const na = Math.max(0, Math.min(255, (a * alphaScale) | 0));
    out[i] = (px & 0x00ffffff) | ((na << 24) >>> 0);
  }
  return { ...frame, data: out };
}

/**
 * Build an ActorSprites set from a single still. The AI stills are single-pose,
 * so walk is one frame (the raycaster's walk index just clamps), pain is a
 * white flash, and die is a short fade sequence — matching the visual language
 * of the procedural baker it replaces.
 */
function actorFromStill(still: SpriteFrame): ActorSprites {
  const walk = [still];
  const pain = tintFlash(still, 0.55);
  const die = [0, 1, 2, 3, 4].map((i) => fade(still, 1 - i * 0.2));
  return { walk, pain, die };
}

/** In-game pixel height for enemy billboards (small = fast + crisp). */
const ENEMY_H = 96;

/**
 * Load the AI sprite roster keyed by archetype kind. Both intruder archetypes
 * map to the hooded threat-actor still; the two "device"-flavoured archetypes
 * map to the rogue-device drone still, so all four spawn kinds render real art.
 * Rejects (and the caller falls back to procedural) if any decode fails.
 */
export async function loadCorridorZeroRoster(): Promise<Map<string, ActorSprites>> {
  const [threat, rogue] = await Promise.all([loadPixels("threatActor"), loadPixels("rogueDevice")]);
  const threatFrame = toFrame(threat, ENEMY_H, 1);
  const rogueFrame = toFrame(rogue, ENEMY_H, 0.92);
  const threatSprites = actorFromStill(threatFrame);
  const rogueSprites = actorFromStill(rogueFrame);
  const map = new Map<string, ActorSprites>();
  // Human-sentinel archetypes -> hooded intruder; infra archetypes -> drone.
  map.set("zeescaler", threatSprites);
  map.set("pale-alto", threatSprites);
  map.set("netscope", rogueSprites);
  map.set("sysko", rogueSprites);
  return map;
}

export interface PickupSprites {
  readonly trustToken: SpriteFrame;
  readonly integrityOrb: SpriteFrame;
  readonly checkpointGate: SpriteFrame;
}

/** Load the world pickup / gate billboards. Rejects on any decode failure. */
export async function loadCorridorZeroPickups(): Promise<PickupSprites> {
  const [token, orb, gate] = await Promise.all([
    loadPixels("trustToken"),
    loadPixels("integrityOrb"),
    loadPixels("checkpointGate"),
  ]);
  return {
    trustToken: toFrame(token, 40, 0.5),
    integrityOrb: toFrame(orb, 40, 0.5),
    checkpointGate: toFrame(gate, 150, 1),
  };
}
